'use client';

/* ============================================================
   HowItWorks — the one explanatory page.

   Deliberately thin. The board explains itself once it's on a screen, so
   this page answers only the questions someone might have BEFORE they
   type a username: what it needs, what it can see, and how to get it onto
   a TV. Everything else is left to the board.

   Screenshots would go between the steps and the FAQ, if you decide the
   board doesn't sell itself.
   ============================================================ */

const AMBER = '#ffb300';
const DIM = '#5c6068';
const TEXT = '#c8cbd0';

const STEPS = [
  {
    n: '01',
    t: 'CONNECT',
    d: 'Type your Sleeper username. We look up the leagues attached to it and you pick one. No password, no login, no account to create.',
  },
  {
    n: '02',
    t: 'SHARE',
    d: 'Your board gets its own link. Drop it in the league group chat and everyone opens the same board — no setup on their end.',
  },
  {
    n: '03',
    t: 'DISPLAY',
    d: 'Press F for fullscreen and leave it up. The board polls for changes on a slow cycle and flips only what actually moved. Press S any time to switch between matchups and standings.',
  },
];

const FAQ = [
  {
    q: 'Does it update play by play?',
    a: 'No, and that is on purpose. Scores refresh on a coarse cycle. A board that never rests is a scoreboard; the flips only mean something because they are rare.',
  },
  {
    q: 'What can it see?',
    a: 'Only what Sleeper already makes public: team names, avatars, records, matchups and scores. It is read-only. Nothing is posted, changed, or written back to your league.',
  },
  {
    q: 'My league is from last season.',
    a: 'It still works. Out of season the board shows final standings for the last week your league actually played, and the header reads FINAL instead of a week number.',
  },
  {
    q: 'Does it work on a TV?',
    a: 'That is the intended use. Open the link in any browser, press F, and the layout scales itself to the screen. Left alone in fullscreen it alternates matchups and standings; the moment you pick a view yourself it stays put.',
  },
  {
    q: 'How do I get it on the TV?',
    a: 'Easiest is to cast a browser tab from Chrome, or open the link in your TV\'s own browser and bookmark it. Any streaming stick with a browser works too. All it needs is a screen and a browser pointed at the link.',
  },
  {
    q: 'Is there a shorter link for typing on a TV remote?',
    a: 'Yes. buttfumbles.com/#sleeper/yourname goes straight to your leagues \u2014 much easier to punch in with a remote than a long league ID. Use it as your own shortcut, then bookmark the board once it loads. To send a board to someone else, use the share link instead: it points at one specific league and will not change on them.',
  },
  {
    q: 'Which leagues are supported?',
    a: 'Sleeper NFL leagues. Other platforms are not supported today.',
  },
];

export default function HowItWorks({ onExit }) {
  return (
    <div style={shell}>
      <div style={{ width: '100%', maxWidth: 640 }}>
        <button type="button" onClick={onExit} style={backBtn}>← BOARD</button>

        <h1 style={title}>HOW IT WORKS</h1>
        <p style={lede}>
          A Solari departures board for your fantasy league. Point it at a
          screen and leave it there.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 38 }}>
          {STEPS.map((s) => (
            <div key={s.n} style={step}>
              <span style={stepNum}>{s.n}</span>
              <div>
                <div style={stepTitle}>{s.t}</div>
                <p style={stepBody}>{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 style={sectionHead}>QUESTIONS</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {FAQ.map((f) => (
            <div key={f.q}>
              <div style={faqQ}>{f.q}</div>
              <p style={faqA}>{f.a}</p>
            </div>
          ))}
        </div>

        <h2 style={sectionHead}>CONTROLS</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <KeyRow k="F" d="Fullscreen" />
          <KeyRow k="S" d="Switch between matchups and standings (stays where you put it)" />
          <KeyRow k="R" d="Refresh now" />
        </div>

        <div style={{ marginTop: 46, paddingTop: 20, borderTop: '1px solid #14161a' }}>
          <button type="button" onClick={onExit} style={cta}>CONNECT A LEAGUE</button>
        </div>
      </div>
    </div>
  );
}

function KeyRow({ k, d }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <kbd style={key}>{k}</kbd>
      <span style={{ color: TEXT, fontSize: 14 }}>{d}</span>
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: '#07080a',
  color: TEXT,
  display: 'flex',
  justifyContent: 'center',
  padding: '6vh 5vw 10vh',
  boxSizing: 'border-box',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};
const backBtn = {
  background: 'transparent', border: 'none', color: DIM, fontSize: 10,
  letterSpacing: '.2em', fontWeight: 700, cursor: 'pointer', padding: '4px 0',
  marginBottom: 30, fontFamily: 'inherit',
};
const title = {
  color: AMBER, fontSize: 15, letterSpacing: '.3em', fontWeight: 700, margin: '0 0 16px',
};
const lede = { color: TEXT, fontSize: 16, lineHeight: 1.6, margin: 0, maxWidth: 480 };
const step = {
  display: 'flex', gap: 18, alignItems: 'flex-start',
  padding: '20px 0', borderBottom: '1px solid #14161a',
};
const stepNum = {
  color: AMBER, fontSize: 11, letterSpacing: '.16em', fontWeight: 700,
  paddingTop: 3, minWidth: 26,
};
const stepTitle = {
  color: '#e8e8e8', fontSize: 12, letterSpacing: '.2em', fontWeight: 700, marginBottom: 7,
};
const stepBody = { color: DIM, fontSize: 14, lineHeight: 1.65, margin: 0 };
const sectionHead = {
  color: AMBER, fontSize: 11, letterSpacing: '.26em', fontWeight: 700,
  margin: '46px 0 22px',
};
const faqQ = { color: '#e8e8e8', fontSize: 14, fontWeight: 700, marginBottom: 7 };
const faqA = { color: DIM, fontSize: 14, lineHeight: 1.65, margin: 0 };
const key = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 27, height: 27, borderRadius: 3, background: '#16181c',
  border: '1px solid #2a2d33', color: AMBER, fontSize: 11, fontWeight: 700,
  fontFamily: 'inherit',
};
const cta = {
  background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER,
  padding: '13px 26px', fontSize: 11, letterSpacing: '.2em', fontWeight: 700,
  cursor: 'pointer', borderRadius: 2, fontFamily: 'inherit',
};
